/**
 * Advanced → Printers — where this application prints when a wizard step
 * says `delivery: "device"`: a label on the dock's Zebra, a receipt on the
 * counter's ESC/POS printer, a pick list on the office laser — silently, with
 * no print dialog.
 *
 * The screen exists to make three things obvious, in this order:
 *
 *  1. A PRINTER IS REACHED THROUGH AN AGENT. The platform cannot open a socket
 *     to a printer on a warehouse LAN; a small program on a machine there
 *     connects out and fetches the jobs. So agents come first, and a printer
 *     with no agent (or an agent that is offline) says so in its row.
 *  2. WHAT EACH KIND OF PRINTER RECEIVES. PDF printers get the rendered
 *     report. ZPL / ESC/POS printers get the report's LABEL TEMPLATE — the
 *     report engine cannot produce either format, and pretending otherwise
 *     prints pages of garbage.
 *  3. WHAT HAPPENED TO EACH JOB. The job history is the answer to "the label
 *     did not come out": queued (no agent took it), sent (taken, no answer
 *     yet), printed, or failed with the printer's own words.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  AlertTriangle, Check, Copy, Download, Loader2, Pencil, Plus, Printer, RefreshCw,
  RotateCcw, Server, Star, Trash2,
} from 'lucide-react'
import {
  addAgent, addPrinter, agentInstallCommand, deleteAgent, deletePrinter,
  downloadAgentFile, getPrinters, listPrintJobs, retryPrintJob, rotateAgentToken,
  testPrinter, updateAgent, updatePrinter,
  type AgentInstall, type CreatedAgent, type PrintJob, type PrinterConnectionType,
  type PrinterInput, type PrinterKind, type PrintersOverview, type ProjectPrinter,
} from '@/api/printers'

const KIND_LABEL: Record<PrinterKind, string> = {
  pdf: 'PDF (documents)',
  zpl: 'ZPL (Zebra labels)',
  escpos: 'ESC/POS (receipts)',
}

const CONN_LABEL: Record<PrinterConnectionType, string> = {
  raw9100: 'Network, raw port 9100',
  cups: 'CUPS queue on the agent machine',
  ipp: 'IPP (network printer)',
  windows: 'Windows printer on the agent machine',
}

const JOB_COLOR: Record<PrintJob['status'], string> = {
  rendering: 'var(--color-text-secondary, #6b7280)',
  queued: 'var(--color-warning, #d97706)',
  sent: 'var(--color-accent)',
  printed: 'var(--color-success, #16a34a)',
  failed: 'var(--color-error, #dc2626)',
}

function errText(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail
  return typeof detail === 'string' && detail ? detail : fallback
}

function when(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

function connText(p: ProjectPrinter): string {
  const c = p.connection || ({} as ProjectPrinter['connection'])
  if (c.type === 'cups') return `CUPS · ${c.queue || ''}`
  if (c.type === 'ipp') return `IPP · ${c.address || ''}`
  if (c.type === 'windows') return `Windows · ${c.printer || ''}`
  return `${c.address || ''}`
}

function CopyBox({ label, text, mono = true }: { label: string; text: string; mono?: boolean }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <pre style={{
          flex: 1, margin: 0, padding: '8px 10px', fontSize: 13, whiteSpace: 'pre-wrap',
          wordBreak: 'break-all', background: 'var(--color-bg-secondary, rgba(0,0,0,0.05))',
          borderRadius: 6, fontFamily: mono ? 'var(--font-mono, monospace)' : undefined,
          maxHeight: 220, overflow: 'auto',
        }}>{text}</pre>
        <button className="btn btn-ghost btn-sm" title="Copy"
          onClick={() => { void navigator.clipboard?.writeText(text); toast.success('Copied') }}>
          <Copy size={14} />
        </button>
      </div>
    </div>
  )
}

const BLANK: PrinterInput = {
  name: '', kind: 'zpl', connection: { type: 'raw9100', address: '' }, agent_id: null,
  is_default: false, description: '',
}

export default function PrintersPage() {
  const { project = '' } = useParams<{ project: string }>()
  const navigate = useNavigate()

  const [data, setData] = useState<PrintersOverview | null>(null)
  const [jobs, setJobs] = useState<PrintJob[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const [agentName, setAgentName] = useState('')
  const [created, setCreated] = useState<CreatedAgent | null>(null)
  const [install, setInstall] = useState<{ agentId: number; install: AgentInstall } | null>(null)
  const [rotated, setRotated] = useState<{ agentId: number; token: string; config: Record<string, unknown> } | null>(null)

  const [editing, setEditing] = useState<{ id: number | null; value: PrinterInput } | null>(null)

  const load = useCallback(async (quiet = false) => {
    if (!project) return
    if (!quiet) setLoading(true)
    try {
      const [overview, jobList] = await Promise.all([getPrinters(project), listPrintJobs(project, 50)])
      setData(overview)
      setJobs(jobList)
    } catch (err) {
      if (!quiet) toast.error(errText(err, 'Could not load this application’s printers'))
    } finally {
      if (!quiet) setLoading(false)
    }
  }, [project])

  useEffect(() => { void load() }, [load])
  useEffect(() => { document.title = `Printers · ${project}` }, [project])

  // Jobs still on their way are worth watching; finished ones are not.
  const inFlight = useMemo(() => jobs.some(j => j.status === 'rendering' || j.status === 'queued' || j.status === 'sent'), [jobs])
  useEffect(() => {
    if (!inFlight) return
    const t = setInterval(() => { void load(true) }, 4000)
    return () => clearInterval(t)
  }, [inFlight, load])

  const run = async (fn: () => Promise<void>, failure: string) => {
    setBusy(true)
    try { await fn() } catch (err) { toast.error(errText(err, failure)) } finally { setBusy(false) }
  }

  // ── agents ──
  const onAddAgent = () => run(async () => {
    const res = await addAgent(project, agentName.trim() || 'Site agent')
    setData(res)
    setCreated(res.created)
    setAgentName('')
  }, 'Could not register the agent')

  const onInstall = (agentId: number) => run(async () => {
    const res = await agentInstallCommand(project, agentId)
    setInstall({ agentId, install: res.install })
  }, 'Could not make an install link')

  const onRotate = (agentId: number) => run(async () => {
    if (!window.confirm('Issue a new token for this agent?\n\nThe old one stops working at once: '
      + 'the agent goes offline until its agent.json carries the new token.')) return
    const res = await rotateAgentToken(project, agentId)
    setRotated({ agentId, ...res })
  }, 'Could not issue a new token')

  const onToggleAgent = (agentId: number, enabled: boolean) => run(async () => {
    setData(await updateAgent(project, agentId, { enabled }))
  }, 'Could not change the agent')

  const onDeleteAgent = (agentId: number) => run(async () => {
    if (!window.confirm('Remove this agent?\n\nIts printers keep their settings but have no '
      + 'agent until you assign another; jobs queued for it wait.')) return
    setData(await deleteAgent(project, agentId))
  }, 'Could not remove the agent')

  // ── printers ──
  const onSavePrinter = () => run(async () => {
    if (!editing) return
    const v = editing.value
    const body: PrinterInput = {
      ...v,
      name: (v.name || '').trim(),
      connection: v.connection?.type === 'cups'
        ? { type: 'cups', queue: (v.connection.queue || '').trim() }
        : v.connection?.type === 'windows'
          ? { type: 'windows', printer: (v.connection.printer || '').trim() }
          : { type: v.connection?.type || 'raw9100', address: (v.connection?.address || '').trim() },
    }
    setData(editing.id == null ? await addPrinter(project, body) : await updatePrinter(project, editing.id, body))
    setEditing(null)
    toast.success('Saved')
  }, 'Could not save the printer')

  const onTest = (p: ProjectPrinter) => run(async () => {
    await testPrinter(project, p.id)
    toast.success(`Test page queued for ${p.name}`)
    await load(true)
  }, 'Could not queue a test page')

  const onDeletePrinter = (p: ProjectPrinter) => run(async () => {
    if (!window.confirm(`Remove printer “${p.name}”?\n\nA wizard that names it will be told `
      + 'there is no such printer.')) return
    setData(await deletePrinter(project, p.id))
  }, 'Could not remove the printer')

  const onMakeDefault = (p: ProjectPrinter) => run(async () => {
    setData(await updatePrinter(project, p.id, { is_default: true }))
  }, 'Could not change the default printer')

  const onRetry = (j: PrintJob) => run(async () => {
    await retryPrintJob(project, j.id)
    await load(true)
  }, 'Could not send the job again')

  if (loading) {
    return (
      <div style={{ padding: 32, display: 'flex', gap: 10, alignItems: 'center' }}>
        <Loader2 size={18} className="spin" />
        <span style={{ color: 'var(--color-text-secondary)' }}>Loading…</span>
      </div>
    )
  }

  const agents = data?.agents || []
  const printers = data?.printers || []
  const ev = editing?.value
  const setEv = (p: Partial<PrinterInput>) => setEditing(e => (e ? { ...e, value: { ...e.value, ...p } } : e))

  return (
    <div style={{ padding: '0 32px 32px', maxWidth: 1040 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/workbench')}>&larr; Back</button>
        <h1 style={{ fontSize: 23, fontWeight: 600, margin: 0 }}>Printers: {project}</h1>
      </div>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, margin: '0 0 20px 44px' }}>
        A wizard step can print a label, a receipt or a document straight to a
        printer at the site — no print dialog. Name the printer in the step
        (or leave it blank for the default below). The platform renders the
        report as the person running the wizard; a print agent on the
        printers&apos; network fetches the job and prints it.
      </p>

      {/* ── Agents ── */}
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Server size={16} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>Print agents</div>
        </div>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 14px' }}>
          One small Python program (standard library only) on any always-on
          machine that can reach the printers. It connects out to this platform
          — no open port, no VPN — and only ever prints what it is sent.
        </p>

        {agents.length === 0 && (
          <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
            No agents yet. Register one, then run its install command on the site machine.
          </div>
        )}
        {agents.map(a => (
          <div key={a.id} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
            borderTop: '1px solid var(--color-border)', flexWrap: 'wrap',
          }}>
            <span title={a.online ? 'Online' : 'Offline'} style={{
              width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
              background: a.online ? 'var(--color-success, #16a34a)' : 'var(--color-text-secondary)',
            }} />
            <div style={{ flex: '1 1 260px', fontSize: 14 }}>
              <div style={{ fontWeight: 500 }}>
                {a.name}{!a.enabled && <span style={{ color: 'var(--color-warning, #d97706)' }}> · switched off</span>}
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {a.last_seen_at
                  ? <>Last seen {when(a.last_seen_at)}{a.hostname ? ` on ${a.hostname}` : ''}{a.version ? ` · v${a.version}` : ''}</>
                  : a.install_pending ? 'Install link issued — waiting for the agent to check in' : 'Never connected'}
              </div>
            </div>
            <button className="btn btn-sm" disabled={busy} onClick={() => void onInstall(a.id)}>Install command</button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void onRotate(a.id)} title="Issue a new token">
              <RotateCcw size={14} />
            </button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void onToggleAgent(a.id, !a.enabled)}>
              {a.enabled ? 'Switch off' : 'Switch on'}
            </button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void onDeleteAgent(a.id)}
              style={{ color: 'var(--color-error, #dc2626)' }} title="Remove">
              <Trash2 size={14} />
            </button>
            {install?.agentId === a.id && (
              <div style={{ flexBasis: '100%' }}>
                <CopyBox label={`Run on the site machine (valid until ${when(install.install.expires_at)}, single use) — Linux, as root`}
                  text={install.install.command} />
                {install.install.windows_command && (
                  <CopyBox label="…or on Windows, in an elevated PowerShell (needs Python 3 for all users)"
                    text={install.install.windows_command} />
                )}
              </div>
            )}
            {rotated?.agentId === a.id && (
              <div style={{ flexBasis: '100%' }}>
                <CopyBox label="New agent.json — shown once" text={JSON.stringify(rotated.config, null, 2)} />
              </div>
            )}
          </div>
        ))}

        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <input className="form-input" style={{ maxWidth: 280 }} placeholder="Agent name, e.g. Warehouse PC"
            value={agentName} onChange={(e) => setAgentName(e.target.value)} />
          <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => void onAddAgent()}>
            <Plus size={14} /> Register agent
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => void downloadAgentFile(project).catch(
            (err) => toast.error(errText(err, 'Could not download the agent')))}>
            <Download size={14} /> Agent file
          </button>
        </div>

        {created && (
          <div className="card" style={{
            padding: 16, marginTop: 16, borderLeft: '3px solid var(--color-accent)',
          }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
              Agent “{created.agent.name}” registered — this is shown once
            </div>
            <CopyBox label="One-command install (systemd Linux with python3; valid one hour)"
              text={created.install.command} />
            {created.install.windows_command && (
              <CopyBox label="…or on Windows: run in an elevated PowerShell (Python 3 installed for all users; same link, same hour)"
                text={created.install.windows_command} />
            )}
            <CopyBox label="Or by hand: /etc/twasta-print/agent.json (keep it 0600)"
              text={JSON.stringify(created.config, null, 2)} />
            <CopyBox label="…and /etc/systemd/system/twasta-print-agent.service" text={created.systemd_unit} />
            <button className="btn btn-sm" onClick={() => setCreated(null)}>Done</button>
          </div>
        )}
      </div>

      {/* ── Printers ── */}
      <div className="card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <Printer size={16} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>Printers</div>
          <button className="btn btn-sm" style={{ marginLeft: 'auto' }} disabled={busy || !!editing}
            onClick={() => setEditing({ id: null, value: { ...BLANK, agent_id: agents[0]?.id ?? null } })}>
            <Plus size={14} /> Add printer
          </button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '0 0 14px' }}>
          A <strong>PDF</strong> printer receives the rendered report. A{' '}
          <strong>ZPL</strong> or <strong>ESC/POS</strong> printer receives the
          report&apos;s label template (<code>REPORT.LABEL_TEMPLATE</code>, with{' '}
          <code>{'{PROMPT}'}</code> placeholders filled from its criteria) — the
          report engine cannot produce either format, so a report without a
          template is refused for such a printer rather than printed as garbage.
        </p>

        {printers.length === 0 && !editing && (
          <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
            No printers yet. A wizard that prints to a device is told “direct
            printing is not set up for this application”.
          </div>
        )}
        {printers.map(p => (
          <div key={p.id} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
            borderTop: '1px solid var(--color-border)', flexWrap: 'wrap', fontSize: 14,
          }}>
            <div style={{ flex: '1 1 280px' }}>
              <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                {p.name}
                {p.is_default && <span title="Default printer"><Star size={13} style={{ color: 'var(--color-accent)' }} /></span>}
                {!p.enabled && <span style={{ fontSize: 13, color: 'var(--color-warning, #d97706)' }}>switched off</span>}
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {KIND_LABEL[p.kind]} · {connText(p)}
              </div>
            </div>
            <div style={{ fontSize: 13, minWidth: 170 }}>
              {p.agent_id
                ? <span style={{ color: p.agent_online ? 'var(--color-success, #16a34a)' : 'var(--color-warning, #d97706)' }}>
                    via {p.agent_name} {p.agent_online ? '(online)' : '(offline)'}
                  </span>
                : <span style={{ color: 'var(--color-error, #dc2626)', display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                    <AlertTriangle size={12} /> No agent — cannot print
                  </span>}
            </div>
            <button className="btn btn-sm" disabled={busy || !p.agent_id} onClick={() => void onTest(p)}>Test page</button>
            {!p.is_default && (
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void onMakeDefault(p)} title="Make default">
                <Star size={14} />
              </button>
            )}
            <button className="btn btn-ghost btn-sm" disabled={busy || !!editing} title="Edit"
              onClick={() => setEditing({ id: p.id, value: {
                name: p.name, description: p.description, kind: p.kind, connection: { ...p.connection },
                agent_id: p.agent_id, enabled: p.enabled, is_default: p.is_default,
              } })}>
              <Pencil size={14} />
            </button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void onDeletePrinter(p)}
              style={{ color: 'var(--color-error, #dc2626)' }} title="Remove">
              <Trash2 size={14} />
            </button>
          </div>
        ))}

        {editing && ev && (
          <div className="card" style={{ padding: 16, marginTop: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>
              {editing.id == null ? 'New printer' : `Edit ${ev.name}`}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label className="form-label">Name (what a wizard step names)</label>
                <input className="form-input" value={ev.name || ''} placeholder="dock-1-labels"
                  onChange={(e) => setEv({ name: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Kind</label>
                <select className="form-input" value={ev.kind}
                  onChange={(e) => setEv({ kind: e.target.value as PrinterKind })}>
                  {(data?.kinds || ['pdf', 'zpl', 'escpos']).map(k => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Connection</label>
                <select className="form-input" value={ev.connection?.type || 'raw9100'}
                  onChange={(e) => setEv({ connection: { type: e.target.value as PrinterConnectionType } })}>
                  {(data?.connection_types || ['raw9100', 'cups', 'ipp', 'windows']).map(t => <option key={t} value={t}>{CONN_LABEL[t]}</option>)}
                </select>
              </div>
              <div>
                {ev.connection?.type === 'cups' ? (
                  <>
                    <label className="form-label">CUPS queue</label>
                    <input className="form-input" value={ev.connection?.queue || ''} placeholder="Zebra_GK420"
                      onChange={(e) => setEv({ connection: { type: 'cups', queue: e.target.value } })} />
                  </>
                ) : ev.connection?.type === 'windows' ? (
                  <>
                    <label className="form-label">Windows printer name</label>
                    <input className="form-input" value={ev.connection?.printer || ''} placeholder="ZDesigner ZD420"
                      title="Sent RAW through the Windows spooler: ZPL / ESC/POS as they are; a PDF only if the printer reads PDF itself (prefer raw 9100 or IPP for PDF)"
                      onChange={(e) => setEv({ connection: { type: 'windows', printer: e.target.value } })} />
                  </>
                ) : (
                  <>
                    <label className="form-label">{ev.connection?.type === 'ipp' ? 'Printer URI' : 'Address'}</label>
                    <input className="form-input" value={ev.connection?.address || ''}
                      placeholder={ev.connection?.type === 'ipp' ? 'ipp://printer.local/ipp/print' : '10.0.0.21:9100'}
                      onChange={(e) => setEv({ connection: { type: ev.connection?.type || 'raw9100', address: e.target.value } })} />
                  </>
                )}
              </div>
              <div>
                <label className="form-label">Agent</label>
                <select className="form-input" value={ev.agent_id ?? ''}
                  onChange={(e) => setEv({ agent_id: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">— none —</option>
                  {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label">Description</label>
                <input className="form-input" value={ev.description || ''} placeholder="Dock 1, 4×6 in labels"
                  onChange={(e) => setEv({ description: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 14, flexWrap: 'wrap' }}>
              <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={!!ev.is_default} onChange={(e) => setEv({ is_default: e.target.checked })} />
                Default printer (used when a step names none)
              </label>
              {editing.id != null && (
                <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={ev.enabled !== false} onChange={(e) => setEv({ enabled: e.target.checked })} />
                  Switched on
                </label>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => void onSavePrinter()}>
                <Check size={14} /> Save
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Jobs ── */}
      <div className="card" style={{ padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Recent print jobs</div>
          {inFlight && <Loader2 size={14} className="spin" />}
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => void load(true)}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
        {jobs.length === 0 ? (
          <div style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>Nothing printed yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)' }}>
                  <th style={{ padding: '4px 6px' }}>#</th>
                  <th style={{ padding: '4px 6px' }}>Status</th>
                  <th style={{ padding: '4px 6px' }}>Report</th>
                  <th style={{ padding: '4px 6px' }}>Printer</th>
                  <th style={{ padding: '4px 6px' }}>Copies</th>
                  <th style={{ padding: '4px 6px' }}>From</th>
                  <th style={{ padding: '4px 6px' }}>Queued</th>
                  <th style={{ padding: '4px 6px' }} />
                </tr>
              </thead>
              <tbody>
                {jobs.map(j => (
                  <tr key={j.id} style={{ borderTop: '1px solid var(--color-border)', verticalAlign: 'top' }}>
                    <td style={{ padding: '6px' }}>{j.id}</td>
                    <td style={{ padding: '6px' }}>
                      <span style={{ color: JOB_COLOR[j.status], fontWeight: 600 }}>{j.status}</span>
                      {j.error && <div style={{ color: 'var(--color-error, #dc2626)', maxWidth: 320 }}>{j.error}</div>}
                    </td>
                    <td style={{ padding: '6px' }}>{j.title || j.obj_name}</td>
                    <td style={{ padding: '6px' }}>{j.printer_name || `#${j.printer_id}`}</td>
                    <td style={{ padding: '6px' }}>{j.copies}</td>
                    <td style={{ padding: '6px' }}>
                      {j.source?.wizard ? `wizard ${j.source.wizard}` : (j.source?.client || '—')}
                    </td>
                    <td style={{ padding: '6px' }}>{when(j.created_at)}</td>
                    <td style={{ padding: '6px' }}>
                      {j.can_retry && (
                        <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void onRetry(j)}>Retry</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
